import React, { useState, useEffect, useContext, useCallback, useRef } from 'react';
import { DateTime } from 'luxon';
import { Environment } from 'src/Environment';

import RefundModal from 'src/containers/self-service/refund-modal/refund-modal';
import { CactusImage } from 'src/assets/images';
import { Card, LoaderAnimation, ShowIf } from 'src/components';
import { TransactionFilterContext } from 'src/context/transaction-filter-context';
import { DownloadCSVContext } from 'src/context/download-csv-context';
import { MerchantProfileContext } from 'src/context/merchant-profile-context';
import { formatCurrencyAmount, formatCurrencyAmountSimple } from 'src/helpers/formatting';
import { transformTransactionsForCsv } from 'src/helpers/csv-string-builder';
import { searchRefunds } from 'src/services/refund.service';
import StatusSpan, { STATUS_TYPE } from 'src/components/status-span/status-span';
import TealiumEvents from 'src/helpers/tealium-events';
import useTechnicalErrorHandler from 'src/hooks/useTechnicalError';
import './refunds.scss';

export default function RefundsTable({ isSearching = false }) {
  const { transactionFilter } = useContext(TransactionFilterContext);
  const { setCsvDataGenerator } = useContext(DownloadCSVContext);
  const { merchant } = useContext(MerchantProfileContext);
  const handleTechnicalError = useTechnicalErrorHandler();
  const [selectedTransaction, setSelectedTransaction] = useState();
  const [transactions, setTransactions] = useState([]);
  const [showRefundsModal, setShowRefundsModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [currentPageNumber, setCurrentPageNumber] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [loaderTriggered, setLoaderTriggered] = useState(false);

  const displayIncrements = 6;
  const [, setDisplayedAmount] = useState(displayIncrements);

  const loadTransactions = async (firstLoad = true) => {
    if (firstLoad) {
      setLoading(true);
      setCurrentPageNumber(1);
    }
    const transactionFetchAbortController = new AbortController();
    const refundsResponse = await searchRefunds(
      transactionFetchAbortController,
      transactionFilter.selectedDates[0].toISOString().split('T')[0],
      transactionFilter.selectedDates[1].toISOString().split('T')[0],
      transactionFilter.transactionSearch.reference || null,
      transactionFilter.transactionSearch.fromAmount || null,
      transactionFilter.transactionSearch.toAmount || null,
      currentPageNumber,
      handleTechnicalError,
      false,
      true,
    );
    if (!transactionFetchAbortController.signal.aborted) {
      const refunds = Environment.current?.disabledFeatures?.pagination
        ? refundsResponse
        : refundsResponse.Rows;
      const numberOfPages = refundsResponse.NumberOfPages;
      if (!!numberOfPages && numberOfPages > 0) {
        setTotalPages(numberOfPages);
      }
      if (refunds) {
        const fullTransactionList = firstLoad ? refunds : transactions.concat(refunds);
        setTransactions(fullTransactionList);
        setCsvDataGenerator(() => {
          // return function to be called only when download/export is clicked
          return async () => {
            return transformTransactionsForCsv(
              merchant.companyName,
              transactionFilter.selectedDates,
              refunds,
              true,
            );
          };
        });
      }
      setDisplayedAmount(displayIncrements);
      setLoading(false);
      setLoaderTriggered(false);
    }
  };

  const loadMorePagesIfAvailable = () => {
    if (!loading && currentPageNumber < totalPages) {
      setLoaderTriggered(true);
      setCurrentPageNumber(currentPageNumber + 1);
    }
  };

  const observer = useRef();
  const nthElementRefDesktop = useCallback(
    (ref) => {
      if (loading || loaderTriggered) return;
      if (observer.current) {
        observer.current.disconnect();
      }
      observer.current = new IntersectionObserver((entries) => {
        if (entries[0].isIntersecting) {
          loadMorePagesIfAvailable();
        }
      });
      if (ref) observer.current.observe(ref);
    },
    [loading, loaderTriggered, currentPageNumber, totalPages],
  );

  const observerMobile = useRef();
  const nthElementRefMobile = useCallback(
    (ref) => {
      if (loading || loaderTriggered) return;
      if (observerMobile.current) {
        observer.current.disconnect();
      }
      observerMobile.current = new IntersectionObserver((entries) => {
        if (entries[0].isIntersecting) {
          loadMorePagesIfAvailable();
        }
      });
      if (ref) observerMobile.current.observe(ref);
    },
    [loading, loaderTriggered, currentPageNumber, totalPages],
  );

  useEffect(() => {
    if (currentPageNumber > 1) {
      loadTransactions(false);
    }
  }, [currentPageNumber]);

  useEffect(() => {
    loadTransactions();
  }, [transactionFilter]);

  useEffect(() => {
    if (selectedTransaction) {
      setShowRefundsModal(true);
    }
  }, [selectedTransaction]);

  useEffect(() => {
    TealiumEvents.transactionRefundsPageLoad();
  }, []);

  const tableHeader = () => {
    return (
      <thead className="table-heading-padding">
        <tr>
          <th className="is-hidden-mobile-portrait cell-padding">Transaction type</th>
          <th className="is-hidden-mobile cell-padding">Ref. no.</th>
          <th className="cell-padding">Date & Time</th>
          {isSearching && <th className="cell-padding">Date</th>}
          <th className="cell-padding text-right">Amount</th>
          <th className="cell-padding">Response code</th>
          <th className="cell-padding text-right">Details</th>
        </tr>
      </thead>
    );
  };

  const MobileView = () => (
    <div className="is-hidden-tablet">
      {transactions &&
        transactions.map((transaction, index) => {
          return (
            <table key={`transaction-${index}`} className="table transaction-details-table">
              <tbody>
                <tr ref={(index + 1) % (60 * currentPageNumber) === 0 ? nthElementRefMobile : null}>
                  <td className="table-data-sub-text">
                    {DateTime.fromISO(transaction.transactionDate).toFormat('HH:mm')}
                  </td>
                  <td></td>
                  <td className="is-pulled-right price-data" style={{ marginRight: '5px' }}>
                    R {formatCurrencyAmountSimple(transaction.transactionAmount)}
                  </td>
                </tr>
                <tr>
                  <td className="table-data-description">
                    {transaction.transactionTypeDescription}
                  </td>
                  <td className="is-pulled-right is-vcentered">
                    <StatusSpan
                      status={
                        transaction.responseCode in STATUS_TYPE
                          ? STATUS_TYPE[transaction.responseCode]
                          : transaction.responseCodeDescription
                      }
                      className="is-pulled-left"
                    />
                  </td>
                  <td>
                    <span
                      className="icon-chevron-right-thin is-icon-5 has-text-grey-light is-pulled-right"
                      onClick={() => {
                        TealiumEvents.clickViewRefunds();
                        setSelectedTransaction(transaction);
                      }}
                    />
                  </td>
                </tr>
                <tr>
                  <td className="table-data-sub-text">
                    Ref no. {transaction.retrievalReferenceNumber}
                  </td>
                </tr>
              </tbody>
            </table>
          );
        })}

      <ShowIf condition={loaderTriggered}>
        <div className="loader">
          <LoaderAnimation />
        </div>
      </ShowIf>

      {loading && (
        <table className="table transaction-details-table">
          <tbody>
            <tr>
              <td className="has-text-centered table-data-description">Loading...</td>
            </tr>
          </tbody>
        </table>
      )}

      {transactions.length < 1 && !loading && (
        <table className="table is-fullwidth">
          <tbody className="no-data-body">
            <tr>
              <td className="has-text-centered no-data-image-container">
                <img src={CactusImage} className="no-data-image" />
              </td>
            </tr>
            <tr>
              <td className="has-text-centered no-data-text-container">
                <span className="title">No data to display</span>
              </td>
            </tr>
          </tbody>
        </table>
      )}
    </div>
  );

  const DesktopView = () => (
    <>
      <Card stretchVertical={true} className="card-table">
        <ShowIf condition={!loading && transactions?.length > 0}>
          <div className="cardContent">
            <table data-testid="transaction-table" className="table is-fullwidth">
              {tableHeader()}
              <tbody>
                {transactions &&
                  transactions.map((transaction, tIndex) => (
                    <tr
                      key={tIndex}
                      ref={
                        (tIndex + 1) % (60 * currentPageNumber) === 0 ? nthElementRefDesktop : null
                      }
                      className={
                        transaction === selectedTransaction
                          ? 'has-background-danger has-text-white'
                          : ''
                      }
                    >
                      <td className="cell-padding">{transaction.transactionTypeDescription}</td>
                      <td className="is-hidden-mobile cell-padding">
                        {transaction.retrievalReferenceNumber}
                      </td>
                      <td className="cell-padding">
                        {DateTime.fromISO(transaction.transactionDate).toFormat(
                          'dd MMMM yyyy HH:mm',
                        )}
                      </td>
                      {isSearching && (
                        <td className="cell-padding">
                          {DateTime.fromISO(transaction.transactionDate).toFormat('dd/MM/yyyy')}
                        </td>
                      )}
                      <td className="cell-padding is-pulled-right">
                        R {formatCurrencyAmount(transaction.transactionAmount)}
                      </td>
                      <td className="cell-padding">
                        <StatusSpan
                          status={
                            transaction.responseCode in STATUS_TYPE
                              ? STATUS_TYPE[transaction.responseCode]
                              : transaction.responseCodeDescription
                          }
                        />
                      </td>
                      <td className="cell-padding-none">
                        <span
                          data-testid={`transaction-${tIndex}-select`}
                          className="is-pulled-right select-refund"
                          onClick={() => {
                            TealiumEvents.clickViewRefunds();
                            setSelectedTransaction(transaction);
                          }}
                        >
                          <span className="icon-chevron-right-thin is-icon-4 has-text-grey-light has-margin-right-0 "></span>
                        </span>
                      </td>
                    </tr>
                  ))}
              </tbody>
              {/* <tfoot key={'scrollThreshold'} ref={loader} /> */}
            </table>
            <ShowIf condition={loaderTriggered}>
              <div className="loader">
                <LoaderAnimation />
              </div>
            </ShowIf>
          </div>
        </ShowIf>
      </Card>
      {transactions.length < 1 && !loading && (
        <Card className="cardContent">
          <table className="table is-fullwidth no-data-table">
            {tableHeader()}
            <tbody>
              <tr>
                <td colSpan="6" className="has-text-centered no-data-image-container">
                  <img src={CactusImage} className="no-data-image" />
                </td>
              </tr>
              <tr>
                <td colSpan="6" className="has-text-centered no-data-text-container">
                  <span className="title">No data to display</span>
                </td>
              </tr>
            </tbody>
          </table>
        </Card>
      )}
      {loading && (
        <Card stretchVertical={true} className="card-table">
          <div className="cardContent">
            <table className="table is-fullwidth">
              <tbody>
                <tr>
                  <td colSpan="6" className="has-text-centered">
                    Loading...
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </Card>
      )}
    </>
  );

  return (
    <div className="RefundsContainer" data-testid="RefundsContainer">
      <RefundModal
        show={showRefundsModal}
        transactionDetails={selectedTransaction}
        onHide={() => {
          setShowRefundsModal(false);
          setSelectedTransaction();
        }}
        methodOfPayment={selectedTransaction?.methodOfPayment}
        disabled={false}
      />
      <div className="is-hidden-tablet">
        <MobileView />
      </div>
      <div className="is-hidden-mobile">
        <DesktopView />
      </div>
      {/* {!loading && displayedAmount < transactions.length && (
        <div
          className="load-more-container"
          onClick={() => {
            // TealiumEvents.terminalTransactionsClickLoadMore();

            const newDisplayedAmount = Math.min(
              displayedAmount + displayIncrements,
              transactions.length,
            );
            setDisplayedAmount(newDisplayedAmount);
          }}
        >
          Load more&nbsp;
          <span className="icon icon-menu-chevron-down is-icon-7"></span>
        </div>
      )} */}
    </div>
  );
}
